import {
  requireKdsHistoryTickets,
  requireKdsTicket,
  requireKdsTickets,
} from '@crab/kds';
import type { ConsoleMessage, LiveOrder, TenantNotification } from '@/core/types/live';
import type { CatalogUpdate } from '@/generated/ts-rs/CatalogUpdate';
import type { StoreInfo } from '@/generated/ts-rs/StoreInfo';
import type { KdsHistoryTicket } from '@/generated/ts-rs/KdsHistoryTicket';

const MESSAGE_TYPES = [
  'Ready',
  'OrderUpdated',
  'OrderRemoved',
  'EdgeStatus',
  'StoreInfoUpdated',
  'DailyReportGenerated',
  'CatalogUpdated',
  'Notification',
  'KdsSnapshot',
  'KdsTicketUpdated',
  'KdsTicketRemoved',
  'KdsError',
  'KdsHistoryPage',
  'ProtocolError',
] as const;

const NOTIFICATION_CATEGORIES = [
  'system',
  'billing',
  'certificate',
  'report',
  'compliance',
  'anomaly',
] as const;

const NOTIFICATION_LEVELS = ['info', 'warning', 'error', 'critical', 'success'] as const;

const CATALOG_UPDATE_KINDS = ['command', 'edge_op', 'sync_resource'] as const;

type ConsoleMessageType = (typeof MESSAGE_TYPES)[number];

export class ConsoleMessageProtocolError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConsoleMessageProtocolError';
  }
}

function fail(path: string, message: string): never {
  throw new ConsoleMessageProtocolError(`${path}: ${message}`);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasOwn(record: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function requireRecord(value: unknown, path: string): Record<string, unknown> {
  if (!isRecord(value)) fail(path, 'must be an object');
  return value;
}

/**
 * Validate that every required key is present. Callers still type-check each
 * field individually. Unknown / extra fields are tolerated so the cloud can add
 * new optional fields to a message without this client rejecting the whole
 * payload (forward-compatible) — only the live view's required shape is enforced.
 */
function requireKeys(
  record: Record<string, unknown>,
  requiredKeys: readonly string[],
  path: string,
): void {
  for (const key of requiredKeys) {
    if (!hasOwn(record, key)) fail(`${path}.${key}`, 'is required');
  }
}

function requireString(record: Record<string, unknown>, key: string, path: string): string {
  if (!hasOwn(record, key)) fail(`${path}.${key}`, 'is required');
  const value = record[key];
  if (typeof value !== 'string') fail(`${path}.${key}`, 'must be a string');
  return value;
}

function requireNullableString(
  record: Record<string, unknown>,
  key: string,
  path: string,
): string | null {
  if (!hasOwn(record, key)) fail(`${path}.${key}`, 'is required');
  const value = record[key];
  if (value === null) return null;
  if (typeof value === 'string') return value;
  fail(`${path}.${key}`, 'must be a string or null');
}

function requireBoolean(record: Record<string, unknown>, key: string, path: string): boolean {
  if (!hasOwn(record, key)) fail(`${path}.${key}`, 'is required');
  const value = record[key];
  if (typeof value !== 'boolean') fail(`${path}.${key}`, 'must be a boolean');
  return value;
}

function requireInteger(record: Record<string, unknown>, key: string, path: string): number {
  if (!hasOwn(record, key)) fail(`${path}.${key}`, 'is required');
  const value = record[key];
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
    fail(`${path}.${key}`, 'must be a non-negative integer');
  }
  return value;
}

function requireArray(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) fail(path, 'must be an array');
  return value;
}

function requireUuid(record: Record<string, unknown>, key: string, path: string): string {
  const value = requireString(record, key, path);
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
    fail(`${path}.${key}`, 'must be a UUID string');
  }
  return value;
}

function requireUuidArray(value: unknown, path: string): string[] {
  return requireArray(value, path).map((entry, index) => {
    if (typeof entry !== 'string') fail(`${path}[${index}]`, 'must be a UUID string');
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(entry)) {
      fail(`${path}[${index}]`, 'must be a UUID string');
    }
    return entry;
  });
}

function requireEnum<T extends readonly string[]>(
  record: Record<string, unknown>,
  key: string,
  values: T,
  path: string,
): T[number] {
  const value = requireString(record, key, path);
  if (!(values as readonly string[]).includes(value)) {
    fail(`${path}.${key}`, `must be one of ${values.join(', ')}`);
  }
  return value as T[number];
}

function requireLiveOrder(value: unknown, path: string): LiveOrder {
  const liveOrder = requireRecord(value, path);
  requireKeys(liveOrder, ['store_id', 'order', 'events'], path);
  requireUuid(liveOrder, 'store_id', path);
  requireArray(liveOrder.events, `${path}.events`);

  const order = requireRecord(liveOrder.order, `${path}.order`);
  requireUuid(order, 'order_id', `${path}.order`);
  const meta = requireRecord(order.meta, `${path}.order.meta`);
  requireInteger(meta, 'updated_at', `${path}.order.meta`);

  return value as LiveOrder;
}

function requireStoreInfo(value: unknown, path: string): StoreInfo {
  const info = requireRecord(value, path);
  requireUuid(info, 'id', path);
  requireString(info, 'name', path);
  return value as StoreInfo;
}

function requireCatalogUpdate(value: unknown, path: string): CatalogUpdate {
  const update = requireRecord(value, path);
  const kind = requireEnum(update, 'kind', CATALOG_UPDATE_KINDS, path);
  switch (kind) {
    case 'command':
    case 'edge_op':
      requireKeys(update, ['kind', 'operation', 'resource_type'], path);
      requireString(update, 'operation', path);
      requireString(update, 'resource_type', path);
      break;
    case 'sync_resource':
      requireKeys(update, ['kind', 'resource_type'], path);
      requireString(update, 'resource_type', path);
      break;
  }
  return value as CatalogUpdate;
}

function requireTenantNotification(value: unknown, path: string): TenantNotification {
  const notification = requireRecord(value, path);
  requireKeys(
    notification,
    [
      'id',
      'tenant_id',
      'store_id',
      'title',
      'message',
      'category',
      'level',
      'is_read',
      'action_url',
      'created_at',
      'expires_at',
    ],
    path,
  );
  requireUuid(notification, 'id', path);
  requireUuid(notification, 'tenant_id', path);
  const storeId = notification.store_id;
  if (storeId !== null) {
    if (typeof storeId !== 'string') fail(`${path}.store_id`, 'must be a UUID string or null');
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(storeId)) {
      fail(`${path}.store_id`, 'must be a UUID string or null');
    }
  }
  requireString(notification, 'title', path);
  requireString(notification, 'message', path);
  requireEnum(notification, 'category', NOTIFICATION_CATEGORIES, path);
  requireEnum(notification, 'level', NOTIFICATION_LEVELS, path);
  requireBoolean(notification, 'is_read', path);
  requireNullableString(notification, 'action_url', path);
  requireInteger(notification, 'created_at', path);
  const expiresAt = notification.expires_at;
  if (expiresAt !== null) {
    if (typeof expiresAt !== 'number' || !Number.isInteger(expiresAt) || expiresAt < 0) {
      fail(`${path}.expires_at`, 'must be a non-negative integer or null');
    }
  }
  return value as TenantNotification;
}

function requireKdsHistoryTicketEntries(value: unknown, path: string): KdsHistoryTicket[] {
  requireKdsHistoryTickets(value, path);
  return value as KdsHistoryTicket[];
}

function baseMessage(value: unknown): { message: Record<string, unknown>; type: ConsoleMessageType } {
  const message = requireRecord(value, 'ConsoleMessage');
  const type = requireEnum(message, 'type', MESSAGE_TYPES, 'ConsoleMessage');
  return { message, type };
}

export function parseConsoleMessage(value: unknown): ConsoleMessage {
  const { message, type } = baseMessage(value);

  switch (type) {
    case 'Ready':
      requireKeys(message, ['type', 'snapshots', 'online_edge_ids'], 'Ready');
      return {
        type,
        snapshots: requireArray(message.snapshots, 'Ready.snapshots').map((snapshot, index) =>
          requireLiveOrder(snapshot, `Ready.snapshots[${index}]`),
        ),
        online_edge_ids: requireUuidArray(message.online_edge_ids, 'Ready.online_edge_ids'),
      };
    case 'OrderUpdated':
      requireKeys(message, ['type', 'snapshot'], 'OrderUpdated');
      return { type, snapshot: requireLiveOrder(message.snapshot, 'OrderUpdated.snapshot') };
    case 'OrderRemoved':
      requireKeys(message, ['type', 'order_id', 'store_id'], 'OrderRemoved');
      return {
        type,
        order_id: requireUuid(message, 'order_id', 'OrderRemoved'),
        store_id: requireUuid(message, 'store_id', 'OrderRemoved'),
      };
    case 'EdgeStatus':
      requireKeys(message, ['type', 'store_id', 'online', 'cleared_order_ids'], 'EdgeStatus');
      return {
        type,
        store_id: requireUuid(message, 'store_id', 'EdgeStatus'),
        online: requireBoolean(message, 'online', 'EdgeStatus'),
        cleared_order_ids: requireUuidArray(message.cleared_order_ids, 'EdgeStatus.cleared_order_ids'),
      };
    case 'StoreInfoUpdated':
      requireKeys(message, ['type', 'store_id', 'info'], 'StoreInfoUpdated');
      return {
        type,
        store_id: requireUuid(message, 'store_id', 'StoreInfoUpdated'),
        info: requireStoreInfo(message.info, 'StoreInfoUpdated.info'),
      };
    case 'DailyReportGenerated':
      requireKeys(
        message,
        ['type', 'store_id', 'report_id', 'business_date'],
        'DailyReportGenerated',
      );
      return {
        type,
        store_id: requireUuid(message, 'store_id', 'DailyReportGenerated'),
        report_id: requireUuid(message, 'report_id', 'DailyReportGenerated'),
        business_date: requireString(message, 'business_date', 'DailyReportGenerated'),
      };
    case 'CatalogUpdated':
      requireKeys(message, ['type', 'store_id', 'update'], 'CatalogUpdated');
      return {
        type,
        store_id: requireUuid(message, 'store_id', 'CatalogUpdated'),
        update: requireCatalogUpdate(message.update, 'CatalogUpdated.update'),
      };
    case 'Notification':
      requireKeys(message, ['type', 'notification'], 'Notification');
      return {
        type,
        notification: requireTenantNotification(message.notification, 'Notification.notification'),
      };
    case 'KdsSnapshot':
      requireKeys(message, ['type', 'store_id', 'tickets'], 'KdsSnapshot');
      return {
        type,
        store_id: requireUuid(message, 'store_id', 'KdsSnapshot'),
        tickets: requireKdsTickets(message.tickets, 'KdsSnapshot.tickets'),
      };
    case 'KdsTicketUpdated':
      requireKeys(message, ['type', 'store_id', 'ticket'], 'KdsTicketUpdated');
      return {
        type,
        store_id: requireUuid(message, 'store_id', 'KdsTicketUpdated'),
        ticket: requireKdsTicket(message.ticket, 'KdsTicketUpdated.ticket'),
      };
    case 'KdsTicketRemoved':
      requireKeys(
        message,
        ['type', 'store_id', 'order_id', 'batch_seq', 'destination_id'],
        'KdsTicketRemoved',
      );
      return {
        type,
        store_id: requireUuid(message, 'store_id', 'KdsTicketRemoved'),
        order_id: requireUuid(message, 'order_id', 'KdsTicketRemoved'),
        batch_seq: requireInteger(message, 'batch_seq', 'KdsTicketRemoved'),
        destination_id: requireUuid(message, 'destination_id', 'KdsTicketRemoved'),
      };
    case 'KdsError':
      requireKeys(message, ['type', 'store_id', 'operation', 'code', 'message'], 'KdsError');
      return {
        type,
        store_id: requireUuid(message, 'store_id', 'KdsError'),
        operation: requireString(message, 'operation', 'KdsError'),
        code: requireInteger(message, 'code', 'KdsError'),
        message: requireString(message, 'message', 'KdsError'),
      };
    case 'KdsHistoryPage':
      requireKeys(message, ['type', 'store_id', 'tickets', 'total'], 'KdsHistoryPage');
      return {
        type,
        store_id: requireUuid(message, 'store_id', 'KdsHistoryPage'),
        tickets: requireKdsHistoryTicketEntries(message.tickets, 'KdsHistoryPage.tickets'),
        total: requireInteger(message, 'total', 'KdsHistoryPage'),
      };
    case 'ProtocolError':
      requireKeys(message, ['type', 'code', 'message'], 'ProtocolError');
      return {
        type,
        code: requireInteger(message, 'code', 'ProtocolError'),
        message: requireString(message, 'message', 'ProtocolError'),
      };
  }
}
